
import { HelpCircle, Users } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SubmissionSearchSelect } from "./SubmissionSearchSelect";
import { ReferralSelector } from "./ReferralSelector";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PreviewVariablesSelectorProps {
  onSubmissionSelect: (submission: any) => void;
  onReferralSelectionChange?: (referralIds: number[]) => void;
  selectedSubmission?: any;
  selectedReferralIds?: number[];
  jotformFormId: string | null;
}

export function PreviewVariablesSelector({
  onSubmissionSelect,
  onReferralSelectionChange,
  selectedSubmission,
  selectedReferralIds = [],
  jotformFormId
}: PreviewVariablesSelectorProps) {
  
  // Obtener count de referidos para la submission seleccionada
  const { data: referralsCount = 0 } = useQuery({
    queryKey: ['referrals-count', selectedSubmission?.db_id],
    queryFn: async () => {
      if (!selectedSubmission?.db_id) return 0;

      const { count, error } = await supabase
        .from('jotform_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('parent_submission_id', selectedSubmission.db_id)
        .eq('is_deleted_in_jotform', false);

      if (error) {
        console.error('Error fetching referrals count:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!selectedSubmission?.db_id
  });

  return (
    <div className="flex flex-col gap-4 py-2">
      {/* Selector de submission principal */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="font-medium text-sm">
            Seleccionar Submission Principal
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="mb-1">
                  <span className="font-semibold">Selecciona una submission del formulario JotForm configurado</span>.
                </p>
                <ul className="list-disc ml-4 mt-2 text-xs text-muted-foreground">
                  <li>
                    Las submissions se sincronizan automáticamente desde JotForm.
                  </li>
                  <li>
                    Las respuestas del formulario estarán disponibles como variables en la plantilla.
                  </li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex items-center gap-2 w-full">
            <SubmissionSearchSelect
              onSelect={onSubmissionSelect}
              jotformFormId={jotformFormId}
            />
          </div>
        </div>
      </div>

      {/* Selector de referidos */}
      {selectedSubmission && referralsCount > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="font-medium text-sm">
                Gestionar Referidos
              </label>
              <Badge variant="secondary" className="text-xs">
                {selectedReferralIds.length} de {referralsCount} seleccionados
              </Badge>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-md">
                  <p className="mb-1">
                    <span className="font-semibold">Selecciona qué referidos incluir en el reporte</span>.
                  </p>
                  <ul className="list-disc ml-4 mt-2 text-xs text-muted-foreground">
                    <li>
                      Por defecto se seleccionan todos los referidos.
                    </li>
                    <li>
                      Los datos de los referidos se usarán para variables y gráficos comparativos.
                    </li>
                    <li>
                      Puedes deseleccionar referidos específicos si no quieres incluirlos.
                    </li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <ReferralSelector
            mainSubmissionId={selectedSubmission.db_id}
            selectedReferralIds={selectedReferralIds}
            onSelectionChange={onReferralSelectionChange || (() => {})}
          >
            <Button variant="outline" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Gestionar Referidos ({selectedReferralIds.length}/{referralsCount})
            </Button>
          </ReferralSelector>
        </div>
      )}

      {/* Información adicional */}
      <div className="text-xs text-gray-500">
        Las variables de la submission seleccionada solo afectan la <b>vista previa</b> y el <b>renderizado del reporte</b>. No modifican la plantilla original.
      </div>
    </div>
  );
}
